/* eslint-disable @typescript-eslint/no-unused-vars */
import type { AlbumAPIInterface } from './api-switcher'
import type { ShareType } from '../pages/ShareAlbum/permission_table'
import type { components } from './auto-gen-types'
import { client } from './client'
import { responseDataConverter, apiPhotoToMyPhoto, apiUserToUser } from './converters'

// Extract share type enum from OpenAPI schema
type APIShareType = components['schemas']['Share']['share_type']

// Mapping between frontend ShareType and API ShareType
const shareTypeToAPI: Record<ShareType, APIShareType> = {
  'invite_only': 'INVITE_ONLY',
  'invite_plus': 'INVITE_PLUS',
  'public': 'PUBLIC',
  'public_lock': 'PUBLIC_PASSWORD',
  'invite_only_public_view': 'INVITE_ONLY_PUBLIC_VIEW',
  'invite_plus_public_view': 'INVITE_PLUS_PUBLIC_VIEW'
}

export const AlbumAPI: AlbumAPIInterface = {
  getAlbum: (album_id, signal) => responseDataConverter(
    client.GET('/album/{AlbumId}', {
      params: {
        path: { AlbumId: album_id }
      },
      signal
    }),
    // Convert API MyAlbum response to expected format
    (album) => ({
      id: album.album_id,
      name: album.album_name,
      thumbnail_url: album.thumbnail_photo_url || '',
      photos: (album.photos || []).map(photo => apiPhotoToMyPhoto(photo)),
      last_append: new Date(album.updated_at * 1000) // Convert Unix timestamp to Date
    })
  ),

  getPhotoFromAlbum: (album_id, photo_id, signal) =>
    // Get individual photo details (used for lazy loading or when photo isn't in album response)
    client.GET('/my/{PhotoId}', {
      params: {
        path: { PhotoId: photo_id }
      },
      signal
    }).then(({ data, error, response }) => {
      if (error) return { error, response }
      return {
        data: {
          ...apiPhotoToMyPhoto(data!),
          favorite: data!.is_favorite || false
        },
        response
      }
    }),

  listAlbums: (signal) => responseDataConverter(
    client.GET('/albums', { signal }),
    (data) => (data.albums || []).map(album => ({
      id: album.album_id,
      name: album.album_name,
      thumbnail_url: album.thumbnail_photo_url || '',
      photos_length: album.photo_count,
      last_append: new Date(album.updated_at * 1000) // Convert Unix timestamp to Date
    }))
  ),

  createAlbum: (name, photo_ids) => responseDataConverter(
    client.POST('/album', {
      body: { name, photo_ids }
    }),
    (album) => ({
      id: album.album_id,
      name: album.album_name,
      thumbnail_url: album.thumbnail_photo_url || '',
      photos: (album.photos || []).map(photo => apiPhotoToMyPhoto(photo)),
      last_append: new Date(album.updated_at * 1000) // Convert Unix timestamp to Date
    })
  ),

  deleteAlbum: (album_id) => responseDataConverter(
    client.DELETE('/album/{AlbumId}', {
      params: {
        path: { AlbumId: album_id }
      }
    }),
    () => undefined
  ),

  updateAlbum: (album_id, data) =>
    // TODO: Album update API endpoint needs proper implementation for name changes
    Promise.resolve({
      data: undefined,
      error: { code: 0, message: 'Not implemented - API endpoint needs body format' },
      response: new Response()
    }),

  setAlbumThumbnail: (album_id, photo_id) =>
    client.PUT('/album/{AlbumId}/thumbnail', {
      params: { path: { AlbumId: album_id } },
      body: { photo_id }
    }).then(({ error, response }) =>
      error
        ? { error: { code: error.code || 0, message: error.message || 'Unknown error' }, response }
        : { response }
    ),

  // Convert album to share - this is a multi-step process
  changeShare: async (album_id, share_data: {
    share_type: ShareType,
    auto_approve: boolean,
    password: string,
    is_sensitive?: boolean
  }) => {
    // 1. Get album details
    const albumResponse = await client.GET('/album/{AlbumId}', {
      params: { path: { AlbumId: album_id } }
    })
    if (albumResponse.error) return {
      error: albumResponse.error,
      response: albumResponse.response
    }

    const album = albumResponse.data

    // 2. Create share with album photos
    // TODO: Need to get photo_ids from album since MyAlbum doesn't include photos
    const shareResponse = await client.POST('/share', {
      body: {
        name: album.album_name,
        share_type: shareTypeToAPI[share_data.share_type],
        photo_ids: [], // TODO: Need separate endpoint to get album photos
        is_sensitive: share_data.is_sensitive
      }
    })
    if (shareResponse.error) return {
      error: shareResponse.error,
      response: shareResponse.response
    }

    // 3. Update share settings if needed
    if (share_data.password || share_data.auto_approve !== undefined) {
      await client.PATCH('/share/{ShareId}', {
        params: { path: { ShareId: shareResponse.data.album_id } },
        body: {
          auto_approve: share_data.auto_approve,
          password: share_data.password || undefined
        }
      })
    }

    // 4. Delete original album
    await client.DELETE('/album/{AlbumId}', {
      params: { path: { AlbumId: album_id } }
    })

    // Return share in expected format
    // TODO: This needs to be updated once we know the actual share response format
    return {
      data: {
        id: shareResponse.data.album_id,
        name: shareResponse.data.name || album.album_name,
        thumbnail_url: shareResponse.data.thumbnail_url || album.thumbnail_photo_url || '',
        photos: [], // TODO: Need to fetch photos separately
        last_append: new Date(album.updated_at * 1000),
        owner: apiUserToUser(shareResponse.data.owner),
        approval_awaiting_users: (shareResponse.data.approval_awaiting_users || []).map(u => apiUserToUser(u.user)),
        invitation_users: [], // TODO: Not yet implemented in real API
        members: (shareResponse.data.members || []).map(apiUserToUser),
        share_type: shareResponse.data.share_type as ShareType,
        auto_approve: shareResponse.data.auto_approve || false,
        password: share_data.password,
        password_version: 1, // TODO: Not in API response
        is_sensitive: shareResponse.data.is_sensitive ?? false
      },
      response: new Response()
    }
  },

  appendPhotosToAlbum: (album_id, photo_ids) =>
    client.POST('/album/{AlbumId}/photos', {
      params: {
        path: { AlbumId: album_id }
      },
      body: { photo_ids }
    }).then(({ error, response }) =>
      error ? { error, response } : { response }
    ),

  removePhotosFromAlbum: (album_id, photo_ids) =>
    client.DELETE('/album/{AlbumId}/photos', {
      params: {
        path: { AlbumId: album_id }
      },
      body: { photo_ids }
    }).then(({ error, response }) =>
      error ? { error, response } : { response }
    ),

  tagsToAlbumPhoto: (album_id, photo_id, in_user_ids) =>
    // TODO: This might need special handling for album context
    responseDataConverter(
      client.POST('/my/{PhotoId}/tag', {
        params: {
          path: { PhotoId: photo_id },
        },
        body: { in_user_ids },
      }),
      (photo) => ({
        ...apiPhotoToMyPhoto(photo),
        favorite: photo.is_favorite || false
      })
    ),

  reactionToAlbumPhoto: (album_id, photo_id, reaction, add) =>
    // TODO: This might need special handling for album context
    responseDataConverter(
      client.POST('/my/{PhotoId}/reaction', {
        params: {
          path: { PhotoId: photo_id }
        },
        body: {
          action: add ? 'add' : 'remove',
          reaction: reaction
        },
      }),
      (photo) => ({
        ...apiPhotoToMyPhoto(photo),
        favorite: photo.is_favorite || false
      })
    )
}